/**
 * Admin leads table for the Owner Wage Calculator (`/app/wage-calculator`).
 * Admin-only — the nav catalog places this under GROWTH.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Mail, UserCheck, Trash2, Clock, Ban, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS_META = {
    pending_verification: { label: "Pending verification", chip: "bg-amber-50 text-amber-800 border-amber-200" },
    active:               { label: "Active",               chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    expiring:             { label: "Expiring soon",        chip: "bg-amber-50 text-amber-900 border-amber-300" },
    expired:              { label: "Expired",              chip: "bg-red-50 text-red-800 border-red-200" },
    converted:            { label: "Converted",            chip: "bg-sky-50 text-sky-800 border-sky-200" },
};

export default function WageCalculatorAdmin() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [q, setQ] = useState("");
    const [busyId, setBusyId] = useState("");

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/wage-calculator/leads");
            setRows(data.rows || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (filter !== "all" && r.status !== filter) return false;
            if (!term) return true;
            return [(r.name || ""), (r.email || ""), (r.business_type || "")].some((s) => s.toLowerCase().includes(term));
        });
    }, [rows, filter, q]);

    const counts = useMemo(() => {
        const c = { all: rows.length, active: 0, expiring: 0, expired: 0, converted: 0, pending_verification: 0 };
        rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
        return c;
    }, [rows]);

    async function action(id, verb, extra = "") {
        setBusyId(id + verb);
        try {
            const url = `/wage-calculator/leads/${id}/${verb}${extra}`;
            await api.post(url, {});
            toast.success(`${verb.charAt(0).toUpperCase() + verb.slice(1)} done`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(""); }
    }
    async function remove(id, email) {
        if (!window.confirm(`Delete trial for ${email}? This can't be undone.`)) return;
        setBusyId(id + "del");
        try {
            await api.delete(`/wage-calculator/leads/${id}`);
            toast.success("Deleted");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(""); }
    }
    async function resend(id, kind) {
        setBusyId(id + "re" + kind);
        try {
            await api.post(`/wage-calculator/leads/${id}/resend-email?kind=${kind}`, {});
            toast.success(`${kind} email sent`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(""); }
    }
    async function downloadCsv() {
        try {
            const r = await api.get("/wage-calculator/leads/export.csv", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "wage-calculator-leads.csv";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div className="space-y-5" data-testid="wc-admin-page">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-xl font-semibold text-[#0F1115]">Wage Calculator leads</h1>
                    <p className="text-[12.5px] text-[#5E636E]">Everyone who's started the free 30-day Owner Wage Calculator trial. Convert, extend, expire, or resend an email with the row actions.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={downloadCsv} className="h-9 text-[12.5px]" data-testid="wc-admin-csv">
                        <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
                    </Button>
                    <a href="/wage-calculator" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[12.5px] hover:bg-zinc-50" data-testid="wc-admin-public">
                        <ExternalLink className="h-3.5 w-3.5" /> Open public page
                    </a>
                </div>
            </header>

            <div className="flex flex-wrap items-center gap-2 border-b border-[#E4E4E7] pb-2">
                {[
                    ["all", "All"],
                    ["active", "Active"],
                    ["expiring", "Expiring"],
                    ["expired", "Expired"],
                    ["converted", "Converted"],
                    ["pending_verification", "Pending"],
                ].map(([k, label]) => (
                    <button key={k} onClick={() => setFilter(k)}
                        className={`text-[12.5px] px-2.5 py-1 rounded-full border ${filter === k ? "bg-[#0F1115] text-white border-[#0F1115]" : "border-[#E4E4E7] bg-white text-[#5E636E] hover:text-[#0F1115]"}`}
                        data-testid={`wc-admin-filter-${k}`}>
                        {label} <span className="ml-1 opacity-70">({counts[k] || 0})</span>
                    </button>
                ))}
                <div className="ml-auto w-72">
                    <Input placeholder="Search name / email / business…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-[13px]" data-testid="wc-admin-search" />
                </div>
            </div>

            {loading ? (
                <div className="py-10 text-center text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin" /></div>
            ) : filtered.length === 0 ? (
                <div className="rounded-md border border-dashed border-[#E4E4E7] bg-white p-8 text-center text-[13px] text-[#5E636E]">
                    No leads{q || filter !== "all" ? " match these filters" : " yet"}. Share the Wage Calculator link to start collecting them.
                </div>
            ) : (
                <div className="rounded-md border border-[#E4E4E7] bg-white overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead className="text-[11px] uppercase tracking-wider text-[#5E636E]">
                            <tr>
                                <th className="text-left px-3 py-2">Name</th>
                                <th className="text-left px-3 py-2">Email · Phone</th>
                                <th className="text-left px-3 py-2">Business</th>
                                <th className="text-left px-3 py-2">Status</th>
                                <th className="text-right px-3 py-2">Days left</th>
                                <th className="text-right px-3 py-2">Calcs</th>
                                <th className="text-right px-3 py-2">Latest $/wk</th>
                                <th className="text-right px-3 py-2 w-72">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => {
                                const sm = STATUS_META[r.status] || STATUS_META.pending_verification;
                                return (
                                    <tr key={r.id} className="border-t border-[#EEEDE7]" data-testid={`wc-admin-row-${r.id}`}>
                                        <td className="px-3 py-2 font-medium text-[#0F1115]">{r.name}</td>
                                        <td className="px-3 py-2 text-[#5E636E]">
                                            <div><a href={`mailto:${r.email}`} className="hover:underline">{r.email}</a></div>
                                            <div className="text-[11px]">{r.phone || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2 text-[#5E636E]">{r.business_type || "—"}</td>
                                        <td className="px-3 py-2"><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${sm.chip}`}>{sm.label}</span></td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.days_left ?? "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.computations_count}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{r.last_recommended_weekly != null ? `$${Number(r.last_recommended_weekly).toLocaleString("en-NZ", { maximumFractionDigits: 0 })}` : "—"}</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="inline-flex gap-1 flex-wrap justify-end">
                                                {r.status === "pending_verification" && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busyId === r.id + "reverification"} onClick={() => resend(r.id, "verification")} data-testid={`wc-admin-resend-verify-${r.id}`}>
                                                        <Mail className="h-3 w-3 mr-1" /> Resend verify
                                                    </Button>
                                                )}
                                                {(r.status === "active" || r.status === "expiring") && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busyId === r.id + "rereminder"} onClick={() => resend(r.id, "reminder")} data-testid={`wc-admin-resend-remind-${r.id}`}>
                                                        <Mail className="h-3 w-3 mr-1" /> Reminder
                                                    </Button>
                                                )}
                                                {r.status !== "converted" && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busyId === r.id + "extend"} onClick={() => action(r.id, "extend", "?days=30")} data-testid={`wc-admin-extend-${r.id}`}>
                                                        <Clock className="h-3 w-3 mr-1" /> +30d
                                                    </Button>
                                                )}
                                                {r.status !== "expired" && r.status !== "converted" && (
                                                    <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={busyId === r.id + "expire"} onClick={() => action(r.id, "expire")} data-testid={`wc-admin-expire-${r.id}`}>
                                                        <Ban className="h-3 w-3 mr-1" /> Expire
                                                    </Button>
                                                )}
                                                {r.status !== "converted" && (
                                                    <Button size="sm" className="h-7 text-[11px] bg-[#0F1115] hover:bg-[#2E2E2E] text-white" disabled={busyId === r.id + "convert"} onClick={() => action(r.id, "convert")} data-testid={`wc-admin-convert-${r.id}`}>
                                                        <UserCheck className="h-3 w-3 mr-1" /> Convert
                                                    </Button>
                                                )}
                                                <Button size="icon" variant="ghost" className="h-7 w-7 text-[#5E636E] hover:text-red-700" onClick={() => remove(r.id, r.email)} data-testid={`wc-admin-delete-${r.id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
